import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useCoffees } from "../hooks/useCoffees";
import { CoffeeCard } from "../components/CoffeeCard";
import { api } from "../lib/api";
import { isTea } from "../types";

export function Dashboard() {
  const { data, refresh } = useCoffees();

  const stats = useMemo(() => {
    if (!data) return null;
    const active = data.filter((c) => !c.archived);
    const coffeeRows = active.filter((c) => !isTea(c.kind));
    const teaRows = active.filter((c) => isTea(c.kind));
    return {
      totalItems: active.length,
      coffeeCount: coffeeRows.length,
      teaBottles: teaRows.reduce((n, c) => n + c.portions, 0),
      coffeePortions: coffeeRows.reduce((n, c) => n + c.portions, 0),
      countries: new Set(active.map((c) => c.country)).size,
      oldest: [...active]
        .filter((c) => c.freeze_date)
        .sort((a, b) => (a.freeze_date! < b.freeze_date! ? -1 : 1))
        .slice(0, 3),
      recent: [...active]
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
        .slice(0, 6),
    };
  }, [data]);

  if (!data || !stats) return <p className="text-paper-500">Loading…</p>;

  const onUse = async (id: number) => { await api.use(id); refresh(); };

  return (
    <div className="space-y-14">
      <section className="space-y-6">
        <div className="eyebrow">The Vault</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight max-w-3xl">
          A quiet ledger of every bag and bottle in storage.
        </h1>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-paper-200 border border-paper-200">
        <Stat label="Coffees" value={stats.coffeeCount} sub={`${stats.coffeePortions} portions`} />
        <Stat label="Tea bottles" value={stats.teaBottles} />
        <Stat label="Items" value={stats.totalItems} />
        <Stat label="Origins" value={stats.countries} />
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="eyebrow">Recently added</div>
            <h2 className="font-display text-2xl">Latest arrivals</h2>
          </div>
          <Link to="/browse" className="text-[11px] uppercase tracking-widest text-paper-500 hover:text-ink">
            All →
          </Link>
        </div>
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {stats.recent.map((c) => <CoffeeCard key={c.id} coffee={c} onUse={onUse} />)}
        </div>
      </section>

      {stats.oldest.length > 0 && (
        <section>
          <div className="mb-6">
            <div className="eyebrow">Earliest in freezer</div>
            <h2 className="font-display text-2xl">Drink soon</h2>
          </div>
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {stats.oldest.map((c) => <CoffeeCard key={c.id} coffee={c} onUse={onUse} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="bg-paper-50 px-6 py-8 flex flex-col gap-2">
      <span className="eyebrow">{label}</span>
      <span className="font-display text-4xl tabular-nums">{value}</span>
      {sub ? <span className="text-xs text-paper-500">{sub}</span> : null}
    </div>
  );
}
